import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { kebabCase } from 'lodash/fp';
import { useMemo, VFC } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { ListStatus } from 'dashboard/models/list';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionListMatchV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

type SupportedListType =
  | ApiModel.ListBrowserFingerprint
  | ApiModel.ListDeviceFingerprint
  | ApiModel.ListCountry
  | ApiModel.ListEmailAddress
  | ApiModel.ListFace
  | ApiModel.ListField
  | ApiModel.ListGeolocation
  | ApiModel.ListGovernmentIdNumber
  | ApiModel.ListIpAddress
  | ApiModel.ListName
  | ApiModel.ListPhoneNumber
  | ApiModel.ListSelfieBackground
  | ApiModel.ListString;

const ListItemFields: Record<SupportedListType, string[]> = {
  [ApiModel.ListBrowserFingerprint]: ['browser-fingerprint'],
  [ApiModel.ListDeviceFingerprint]: ['device-fingerprint'],
  [ApiModel.ListCountry]: ['country-code'],
  [ApiModel.ListEmailAddress]: ['email-address'],
  [ApiModel.ListFace]: ['selfies'],
  [ApiModel.ListField]: ['field'],
  [ApiModel.ListGeolocation]: ['geolocations'],
  [ApiModel.ListGovernmentIdNumber]: ['id-class', 'id-number'],
  [ApiModel.ListIpAddress]: ['ip-addresses'],
  [ApiModel.ListName]: ['name-first', 'name-last'],
  [ApiModel.ListPhoneNumber]: ['phone-number'],
  [ApiModel.ListSelfieBackground]: ['selfies'],
  [ApiModel.ListString]: ['string'],
};

const OnMatchParamLabelContainer = styled('div')`
  display: flex;
  gap: ${Spacing[1]};
`;

interface ParamsListMatchV1Props extends ParamsBaseProps {
  lists: WorkflowActionMeta['lists'];
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionListMatchV1['params'];
  updateActionParams: (newParams: WorkflowActionListMatchV1['params']) => void;
}

const ParamsListMatchV1: VFC<ParamsListMatchV1Props> = ({
  lists,
  editable,
  errors,
  usableObjects,
  params,
  updateActionParams,
  updateStep,
  step,
}) => {
  const hasListPerm = usePermission(Permission.ListView);
  const isTemplateAvailable = useMemo(() => {
    return !params['list-id'] || lists.map((list) => list.id).includes(params['list-id']);
  }, [lists, params]);

  const availableLists = useMemo(() => {
    return (
      lists
        ?.filter(
          (list) =>
            list.attributes['status'] === ListStatus.Active &&
            ListItemFields[list.type as SupportedListType],
        )
        .map((list) => ({
          label: list.name,
          value: list.id,
        })) || []
    );
  }, [lists]);

  const updateList = (listId: WorkflowActionListMatchV1['params']['list-id']) => {
    const listName = availableLists.find((l) => l.value === listId)?.label;
    updateStep({
      ...step,
      name: `List Match: ${listName}`,
      action: {
        ...(step.action as WorkflowActionListMatchV1),
        params: {
          ...params,
          'list-id': listId,
          'list-item-fields': {},
        },
      },
    });
  };

  const updateListItemFields = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'list-item-fields': Object.keys(value).reduce<Record<string, string>>(
        (acc, elem) => ({
          ...acc,
          [elem]: value[elem],
        }),
        {},
      ),
    });
  };

  const updateActionOnMatch = (action: 'match' | 'nothing') => {
    const { 'list-matchable': _, ...remainingParams } = params;
    updateActionParams({
      ...remainingParams,
      ...(action === 'match' && possibleObjects[0]
        ? { 'list-matchable': possibleObjects[0].value }
        : {}),
    });
  };

  const updateListMatchable = (object: WorkflowActionListMatchV1['params']['list-matchable']) => {
    updateActionParams({
      ...params,
      'list-matchable': object,
    });
  };

  const [
    showFieldMappingsPanel,
    { toggle: toggleFieldMappingsPanel, disable: closeFieldMappingsPanel },
  ] = useToggle(false);

  const fieldOptions = (
    ListItemFields[lists.find((l) => l.id === params['list-id'])?.type as SupportedListType] || []
  ).reduce<Record<string, string>>(
    (acc, field) => ({
      ...acc,
      [kebabCase(field)]: field,
    }),
    {},
  );

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionListMatchV1['params']>(errors);

  const possibleObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [
          WorkflowFieldObjectKey.Account,
          WorkflowFieldObjectKey.Inquiry,
          WorkflowFieldObjectKey.Transaction,
        ],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='List'
        htmlFor='params-add-item-to-list__list'
        helpText={
          hasListPerm
            ? isTemplateAvailable
              ? undefined
              : 'The current list is not available to you at this time.'
            : 'Your role does not have permissions to view lists.'
        }
      />
      <Select
        value={params['list-id']}
        onValueChange={updateList}
        disabled={!editable || !hasListPerm}
        placeholder={hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'}
        hasError={hasErrorFor('list-id')}
        id='params-add-item-to-list__list'
      >
        {availableLists.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('list-id')}
      <FeatureFlagged featureFlag='dashboard:decouple-list-matches-from-inquiries'>
        <Spacer height={2} />
        <OnMatchParamLabelContainer>
          <ParamLabel labelText='On Match' htmlFor='params-add-item-to-list__action-on-match' />
          <Tooltip
            side='top'
            iconTrigger={<CircleHelpIcon size={14} />}
            aria-label='Create Match will associate matches to a Persona object.'
          >
            <Paragraph>Create Match will associate matches to a Persona object.</Paragraph>
          </Tooltip>
        </OnMatchParamLabelContainer>
        <Select
          value={params['list-matchable'] ? 'match' : 'nothing'}
          onValueChange={updateActionOnMatch}
          disabled={!editable || !hasListPerm}
          placeholder={
            hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'
          }
          id='params-add-item-to-list__action-on-match'
        >
          <Option value='nothing'>Do Nothing</Option>
          <Option value='match'>Create Match</Option>
        </Select>
        {params['list-matchable'] && (
          <>
            <Spacer height={2} />
            <ParamLabel labelText='Object' htmlFor='params-add-item-to-list__list-matchable' />
            <Select
              value={params['list-matchable']}
              onValueChange={updateListMatchable}
              disabled={!editable || !hasListPerm}
              placeholder={
                hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'
              }
              id='params-add-item-to-list__list-matchable'
            >
              {possibleObjects.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </>
        )}
      </FeatureFlagged>
      <Spacer height={2} />
      {isTemplateAvailable && (
        <Accordion title='Item Fields' defaultExpanded>
          <Spacer height={2} />

          {showFieldMappingsPanel && (
            <FieldMappingsPanel
              title='List Item Fields'
              usableObjects={usableObjects}
              fieldMappings={params['list-item-fields'] ?? {}}
              presetFields={fieldOptions}
              onOutsideClick={closeFieldMappingsPanel}
              onUpdateFieldMappings={updateListItemFields}
            />
          )}
          <FieldsList
            fields={params['list-item-fields']}
            fieldKey='list-item-fields'
            toggleEditPanel={toggleFieldMappingsPanel}
            editable={editable && Object.keys(fieldOptions).length > 0}
            placeholderEmptyText={
              Object.keys(fieldOptions).length === 0
                ? 'List has no fields configuration'
                : 'No fields selected'
            }
            errors={errors}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
          />
          {renderErrorMessageFor('list-item-fields')}
        </Accordion>
      )}
    </>
  );
};

export default ParamsListMatchV1;
